import { jsx } from "react/jsx-runtime";
import * as React from "react";
import { parseMinor, currencySymbol, formatMinor, formatMinorForEdit } from "../data/money.js";
import { cx } from "../internal/cx.js";
import { Input } from "./Input.js";
import { UnitInput } from "./UnitInput.js";
function attempt(fn) {
  try {
    return fn();
  } catch {
    return null;
  }
}
function useMoneyInput({
  valueMinor,
  onChange,
  onValidityChange,
  currency,
  locale,
  fractionDigits,
  allowExpression,
  parse,
  onFocus,
  onBlur
}) {
  const [draft, setDraft] = React.useState(null);
  const [invalid, setInvalid] = React.useState(false);
  const loc = { currency, locale, ...fractionDigits !== void 0 ? { fractionDigits } : {} };
  const editable = (v) => attempt(() => formatMinorForEdit(v, loc)) ?? String(v);
  const idle = (v) => attempt(() => formatMinor(v, { ...loc, currencyDisplay: "none" })) ?? editable(v);
  const shown = draft ?? (valueMinor === null ? "" : idle(valueMinor));
  const setValidity = (nextInvalid) => {
    if (nextInvalid !== invalid) onValidityChange?.(!nextInvalid);
    setInvalid(nextInvalid);
  };
  return {
    invalid,
    controlProps: {
      type: "text",
      inputMode: "decimal",
      autoComplete: "off",
      value: shown,
      className: "ev-money-input",
      onFocus: (event) => {
        setDraft((d) => d ?? (valueMinor === null ? "" : editable(valueMinor)));
        onFocus?.(event);
      },
      onChange: (event) => {
        const text = event.target.value;
        setDraft(text);
        if (text.trim() === "") {
          setValidity(false);
          onChange(null);
          return;
        }
        let parsed;
        try {
          parsed = parse ? parse(text) : parseMinor(text, { ...loc, ...allowExpression ? { allowExpression } : {} });
        } catch {
          parsed = null;
        }
        setValidity(parsed === null);
        onChange(parsed);
      },
      onBlur: (event) => {
        if (!invalid) setDraft(null);
        onBlur?.(event);
      }
    }
  };
}
const MoneyInput = React.forwardRef(function MoneyInput2({ valueMinor, onChange, onValidityChange, currency, locale, fractionDigits, allowExpression, parse, unit, wrapperClassName, onFocus, onBlur, className, ...rest }, ref) {
  const { controlProps, invalid } = useMoneyInput({
    valueMinor,
    onChange,
    currency,
    locale,
    ...onValidityChange ? { onValidityChange } : {},
    ...fractionDigits !== void 0 ? { fractionDigits } : {},
    ...allowExpression ? { allowExpression } : {},
    ...parse ? { parse } : {},
    ...onFocus ? { onFocus } : {},
    ...onBlur ? { onBlur } : {}
  });
  const props = {
    ...rest,
    ...controlProps,
    "aria-invalid": rest["aria-invalid"] ?? (invalid ? true : void 0),
    className: cx(controlProps.className, className)
  };
  if (unit === false) return /* @__PURE__ */ jsx(Input, { ref, ...props });
  const shownUnit = unit ?? attempt(() => currencySymbol(currency, locale)) ?? currency;
  return /* @__PURE__ */ jsx(UnitInput, { ref, ...props, unit: shownUnit, ...wrapperClassName ? { wrapperClassName } : {} });
});
MoneyInput.displayName = "MoneyInput";
export {
  MoneyInput,
  useMoneyInput
};
//# sourceMappingURL=MoneyInput.js.map
